/**
 * useFeatureGate(feature) -> { allowed, requiredPlan, requiredPlanLabel, openUpgrade }
 *
 * One way to lock a premium control: read `allowed`, and when it is false show
 * the control locked (<PlanLockBadge />) and call `openUpgrade()` on tap. That
 * opens the focused DirectUpgradeSheet on the cheapest plan with the feature
 * (requests without a feature still use UpgradePlanSheet, whose button opens
 * the same focused sheet). Server gate errors (402/403, lib/gateErrors) route to the same sheet
 * via `useUpgradePrompt().openForError(err)`.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useOrganization } from '@/contexts/OrganizationContext';
import { gateErrorPrompt } from '@/lib/gateErrors';
import {
  isFeatureAllowed,
  requiredPlanFor,
  tierLabel,
  upgradeHeadline,
  upgradeSheetContent,
  type GatedFeature,
  type PlanTier,
} from '@/lib/featureGate';
import { UpgradePlanSheet } from '@/components/mobile/UpgradePlanSheet';
import { useDirectUpgrade } from '@/components/billing/DirectUpgradeSheet';

export interface UpgradeRequest {
  feature?: GatedFeature;
  title: string;
  message?: string;
}

interface UpgradeContextValue {
  open: (req: UpgradeRequest) => void;
}

const UpgradeContext = createContext<UpgradeContextValue | null>(null);

/**
 * Mount once (MobileApp). `onChoosePlan` opens the Billing plan picker; without
 * one the sheet falls back to /settings/billing.
 */
export function UpgradePlanProvider({ onChoosePlan, children }: { onChoosePlan?: () => void; children: ReactNode }) {
  // The last request is kept after closing so the sheet keeps its text while
  // it animates out; clearing it blanked the panel down to its two buttons.
  const [request, setRequest] = useState<UpgradeRequest | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [, setLocation] = useLocation();
  const direct = useDirectUpgrade();
  const open = useCallback((req: UpgradeRequest) => {
    // Nothing to explain → no sheet (upgradeSheetContent is the same check the sheet makes).
    if (!upgradeSheetContent(req)) return;
    // A known feature means a known plan: offer that plan directly (the
    // focused sheet leads with the feature and links to the full grid).
    if (req.feature && direct.available) {
      direct.openDirectUpgrade({ feature: req.feature, source: 'feature_gate' });
      return;
    }
    setRequest(req);
    setIsOpen(true);
  }, [direct.available, direct.openDirectUpgrade]);
  const value = useMemo(() => ({ open }), [open]);
  const choose = () => {
    setIsOpen(false);
    if (direct.available) direct.openDirectUpgrade({ feature: request?.feature, source: 'upgrade_prompt' });
    else if (onChoosePlan) onChoosePlan();
    else setLocation('/settings/billing');
  };
  return (
    <UpgradeContext.Provider value={value}>
      {children}
      <UpgradePlanSheet
        open={isOpen && !!request}
        onOpenChange={(o) => { if (!o) setIsOpen(false); }}
        feature={request?.feature}
        title={request?.title ?? ''}
        message={request?.message}
        onUpgrade={choose}
      />
    </UpgradeContext.Provider>
  );
}

/** Open the upgrade sheet directly, or from a server gate error. */
export function useUpgradePrompt() {
  const ctx = useContext(UpgradeContext);
  const [, setLocation] = useLocation();
  const open = useCallback((req: UpgradeRequest) => {
    if (ctx) ctx.open(req);
    else setLocation('/settings/billing');
  }, [ctx, setLocation]);
  /** True when `err` was a plan gate and the sheet was opened for it. */
  const openForError = useCallback((err: unknown, feature?: GatedFeature): boolean => {
    const gate = gateErrorPrompt(err);
    if (!gate || !gate.upgrade) return false;
    open({ feature, title: feature ? upgradeHeadline(feature) : gate.title, message: gate.message });
    return true;
  }, [open]);
  return { open, openForError, hasProvider: !!ctx };
}

export interface FeatureGate {
  allowed: boolean;
  /** Still resolving the plan; treat as allowed so nothing flashes locked. */
  isLoading: boolean;
  requiredPlan: PlanTier | null;
  requiredPlanLabel: string | null;
  openUpgrade: () => void;
}

export function useFeatureGate(feature: GatedFeature): FeatureGate {
  const access = useFeatureAccess() as any;
  const { capabilities, isLoading: capsLoading } = useCapabilities();
  const { currentOrganization } = useOrganization();
  const { open } = useUpgradePrompt();
  const planType = (currentOrganization as any)?.currentPlan?.type || (currentOrganization as any)?.planType || null;
  const isLoading = !!access.isLoading || capsLoading;
  const allowed = isLoading
    ? true
    : isFeatureAllowed(feature, { planType, can: access.can, caps: capabilities as any });
  const requiredPlan = requiredPlanFor(feature);
  // A plan that already has the feature never sees an upgrade sheet for it.
  const openUpgrade = useCallback(
    () => { if (!allowed) open({ feature, title: upgradeHeadline(feature) }); },
    [open, feature, allowed],
  );
  return { allowed, isLoading, requiredPlan, requiredPlanLabel: tierLabel(requiredPlan), openUpgrade };
}
